import { logAmplitudeEvent } from 'amplitude/amplitude'
import { orgSelectors } from 'ducks/orgs'
import { useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import restClient from 'restClient'
import { useFeatureFlag } from 'util/featureFlags'
import BasicFilter from '../basic/BasicFilter'
import type {
  ConfigurableFilterOptionsType,
  FilterComponentNodeV2,
  FilterGenericPropsType,
  FilterValuesType,
  OptionGenericType,
} from '../typeV2'

const restClientInstance = restClient(window.API_ROOT + '/api')

type AvailableFinancier = 'lightreach' | 'loanpal'

export const FINANCIER_APPROVED_VENDORS_FILTER_NODE_KEY_V2 = 'financier_approved_vendors_filter_key'
export const LIGHTREACH_FILTER_KEY = 'lightreach_approved'
export const LOANPAL_FILTER_KEY = 'loanpal_approved'
export const NO_FILTER_VALUE = 'no_filter'

export const NO_FILTER: OptionGenericType<string> = {
  id: NO_FILTER_VALUE,
  value: NO_FILTER_VALUE,
  title: 'No Filter',
}

export const createFinancierApprovedVendorsFilterNode = <T,>({
  label = 'Financier Approved Vendors',
  rendererComponent: RendererComponent,
  formatter,
  rendererAdditionalProps,
  ...restConfigs
}: ConfigurableFilterOptionsType<string, Set<string>>): FilterComponentNodeV2 => {
  const FilterComponent = (filterProps: FilterGenericPropsType) => {
    const translate = useTranslate()
    const isLightreachFilterEnabled = useFeatureFlag('avl_hardware_filter_lightreach', 'on')
    const isLoanpalFilterEnabled = useFeatureFlag('avl_hardware_filter_loanpal', 'on')
    const orgId = useSelector(orgSelectors.getOrg)?.id
    const availableIntegrations = useSelector(orgSelectors.getAvailableFinanceIntegrations)
    const [availableFinanciers, setAvailableFinanciers] = useState<AvailableFinancier[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const getDisplayName = (integrationName: string, fallback: string) => {
      const integration = availableIntegrations?.find(
        (i: { integration_name: string }) => i.integration_name === integrationName
      )
      return integration?.display_name || fallback
    }

    useEffect(() => {
      if (!orgId) {
        setIsLoading(false)
        return
      }

      const url = `partner_data_matching_record_mappings/available_financiers/?org_id=${orgId}`

      restClientInstance('CUSTOM_GET', 'custom', { url })
        .then((response) => {
          const data = response?.data || response
          setAvailableFinanciers(Array.isArray(data) ? data : [])
          setIsLoading(false)
        })
        .catch((error) => {
          console.error('FinancierFilter: Error fetching available financiers:', error)
          setIsLoading(false)
        })
    }, [orgId])

    if (!isLightreachFilterEnabled && !isLoanpalFilterEnabled) {
      return null
    }

    if (isLoading) {
      return null
    }

    if (availableFinanciers.length === 0) {
      return null
    }

    const options: OptionGenericType<string>[] = [NO_FILTER]

    if (isLightreachFilterEnabled && availableFinanciers.includes('lightreach')) {
      options.push({
        id: LIGHTREACH_FILTER_KEY,
        value: LIGHTREACH_FILTER_KEY,
        title: getDisplayName('lightreach', 'Lightreach'),
      })
    }

    if (isLoanpalFilterEnabled && availableFinanciers.includes('loanpal')) {
      options.push({
        id: LOANPAL_FILTER_KEY,
        value: LOANPAL_FILTER_KEY,
        title: getDisplayName('loanpal', 'Loanpal'),
      })
    }

    if (options.length <= 1) {
      return null
    }

    const sideEffect = ({ filterValues }: { filterValues: FilterValuesType }) => {
      const newFilterValue = { ...filterValues }

      // Parse the selected values using the formatter
      const selectedValues =
        formatter?.parse?.(filterValues[FINANCIER_APPROVED_VENDORS_FILTER_NODE_KEY_V2]) || new Set<string>()

      // Clear all financier filters first
      delete newFilterValue[LIGHTREACH_FILTER_KEY]
      delete newFilterValue[LOANPAL_FILTER_KEY]

      // If "None" is selected or nothing selected, don't set any filters
      const selectedPartners: string[] = []
      if (selectedValues.has(NO_FILTER.value) || selectedValues.size === 0) {
        delete newFilterValue[FINANCIER_APPROVED_VENDORS_FILTER_NODE_KEY_V2]
      } else {
        // Set individual flags based on selection
        if (selectedValues.has(LIGHTREACH_FILTER_KEY)) {
          newFilterValue[LIGHTREACH_FILTER_KEY] = true
          selectedPartners.push('lightreach')
        }
        if (selectedValues.has(LOANPAL_FILTER_KEY)) {
          newFilterValue[LOANPAL_FILTER_KEY] = true
          selectedPartners.push('loanpal')
        }
      }

      logAmplitudeEvent('filter_usage_avl', {
        action: selectedPartners.length > 0 ? 'applied' : 'cleared',
        selected_partners: selectedPartners,
        available_partners: availableFinanciers,
      })

      return newFilterValue
    }

    // Determine default selected values based on current filter state
    let defaultValue = new Set([NO_FILTER.value])
    const currentFilters = filterProps.allFilterValues
    const hasAnyFinancierFilter = currentFilters[LIGHTREACH_FILTER_KEY] || currentFilters[LOANPAL_FILTER_KEY]

    if (hasAnyFinancierFilter) {
      defaultValue = new Set<string>()
      if (currentFilters[LIGHTREACH_FILTER_KEY]) {
        defaultValue.add(LIGHTREACH_FILTER_KEY)
      }
      if (currentFilters[LOANPAL_FILTER_KEY]) {
        defaultValue.add(LOANPAL_FILTER_KEY)
      }
    }

    return (
      <BasicFilter<string, Set<string>>
        label={label}
        rendererComponent={RendererComponent}
        defaultSelectedOptionValue={defaultValue}
        filterKey={FINANCIER_APPROVED_VENDORS_FILTER_NODE_KEY_V2}
        options={options}
        formatter={formatter}
        rendererAdditionalProps={rendererAdditionalProps}
        sideEffect={sideEffect}
        {...restConfigs}
        {...filterProps}
      />
    )
  }

  return {
    key: FINANCIER_APPROVED_VENDORS_FILTER_NODE_KEY_V2,
    component: FilterComponent,
    getMatchedValueFromSpecsData: () => undefined,
  }
}

export default createFinancierApprovedVendorsFilterNode
